import { BreakpointState } from '@angular/cdk/layout';
import { AfterViewChecked, Component, HostBinding, Input } from '@angular/core';
import { IdtComponent } from 'shared/models/idt-components/idt-component.models';
import { ResponsiveSetting } from 'shared/responsive-settings/responsive-settings.interface';
import { AmandaOptions } from 'shared/services/assets-shared.service';

import { SliceBaseHostStyles, SliceBaseRenderComponent } from 'extensions/core/slice-base/render/slice-base-render.component';
import { SliceTabsSettings, TabIcon } from 'extensions/core/slice-tabs/front/settings/slice-tabs-settings.model';


@Component({
  selector: 'slice-tabs-render',
  templateUrl: './slice-tabs-render.component.html',
  styleUrls: ['./slice-tabs-render.component.scss']
})
export class SliceTabsRenderComponent extends SliceBaseRenderComponent implements AfterViewChecked {

  @Input() public slice!: IdtComponent<SliceTabsSettings>;

  public currentTab!: number;
  public tabsRange!: number[];
  public loadedTabs!: Record<number, boolean>;

  private activeTabIsCenteredInScrollbar?: boolean;

  @HostBinding('style.--tab-vertical-position')
  public get tabVerticalPosition(): string {
    return this.slice.settings?.general.tabPosition.includes('bottom') ? 'column-reverse' : 'column';
  }

  @HostBinding('style.--tab-horizontal-position')
  public get tabHorizontalPosition(): string {
    if (this.slice.settings?.general.tabPosition.endsWith('center')) {
      return 'center';
    }
    if (this.slice.settings?.general.tabPosition.endsWith('right')) {
      return 'flex-end';
    }
    return 'flex-start';
  }

  @HostBinding('class')
  public get classes(): string {
    let classes: string = (this.slice?.settings?.styles?.shadow && this.slice.settings.styles.shadow !== 'none') ? `${this.slice.settings.styles?.shadow} ` : '';

    if (this.slice?.settings?.styles?.borderRadiusType) {
      if (this.slice.settings.styles.borderRadiusType === 'straight') {
        classes += 'border-radius-straight';
      } else if (this.slice.settings.styles.borderRadiusType === 'semi-round') {
        classes += 'border-radius-semi-round';
      } else if (this.slice.settings.styles.borderRadiusType === 'round') {
        classes += 'border-radius-round';
      }
    }

    return classes + (this.tabThemeClasses ? this.slice.settings!.styles.tabAppearance.classes! : '');
  }

  @HostBinding('class.tab-theme-box')
  public get tabThemeBox(): boolean {
    return this.slice.settings?.styles.tabAppearance.theme === 'box';
  }

  @HostBinding('class.tab-theme-rounded')
  public get tabThemeRounded(): boolean {
    return this.slice.settings?.styles.tabAppearance.theme === 'rounded';
  }

  @HostBinding('class.tab-theme-solid')
  public get tabThemeSolid(): boolean {
    return this.slice.settings?.styles.tabAppearance.theme === 'solid';
  }

  @HostBinding('class.tab-theme-classes')
  public get tabThemeClasses(): boolean {
    return this.slice.settings?.styles.tabAppearance.theme === 'classes';
  }

  public get tabTypeHasIcon(): boolean {
    return !!this.slice.settings?.general.tabType.includes('icon');
  }

  public get tabTypeHasText(): boolean {
    return !!this.slice.settings?.general.tabType.includes('text');
  }

  public ngAfterViewChecked(): void {
    // If there are several tabs with scrollbar, the selected tab is displayed in center.
    if (!this.activeTabIsCenteredInScrollbar) {
      const tabsWrapperElement = this.el.nativeElement.querySelector('.tabs-wrapper') as HTMLElement;
      const activeTabElement = this.el.nativeElement.querySelector('.tab.selected') as HTMLElement;
      if (tabsWrapperElement && activeTabElement) {
        const offsetCenterPosition = (this.el.nativeElement.offsetWidth - activeTabElement.offsetWidth) / 2;
        tabsWrapperElement.scrollLeft = activeTabElement.offsetLeft - offsetCenterPosition;
        this.activeTabIsCenteredInScrollbar = true;
      }
    }
  }

  public getTabIconSrc(icon: TabIcon, isActiveTab: boolean): string | undefined {
    const iconUrl = isActiveTab && icon.activeIcon ? icon.activeIcon.image : icon.icon.image;
    const assetsUrl = this.renderConfigurationService.config.assetsUrl;
    const akamaiOptions: AmandaOptions = { isWidthParamEnabled: false };

    return this.assetsSharedService.getAssetUrl(iconUrl!, assetsUrl, akamaiOptions);
  }

  public goToTab(tab: number): void {
    this.currentTab = tab;
    this.loadedTabs[this.currentTab] = true;
  }

  public trackByIndex(index: number): number {
    return index;
  }

  protected onMediaChange(breakpointState: BreakpointState): void {
    super.onMediaChange(breakpointState);

    this.initTabs();
  }

  protected onSliceInit(): void { /* empty */ }

  protected setDefaultHostStyles(): void {
    this.setHostStyles({
      backgroundColor: this.slice.settings!.styles.bgColor || '',
      marginTop: this.slice.settings!.styles.marginTop || '',
      marginRight: this.slice.settings!.styles.marginRight || '',
      marginLeft: this.slice.settings!.styles.marginLeft || '',
      marginBottom: this.slice.settings!.styles.marginBottom || '',
      paddingTop: this.slice.settings!.styles.paddingTop,
      paddingRight: this.slice.settings!.styles.paddingRight,
      paddingLeft: this.slice.settings!.styles.paddingLeft,
      paddingBottom: this.slice.settings!.styles.paddingBottom
    });
  }

  // eslint-disable-next-line complexity
  protected setResponsiveHostStyles(
    breakpointStyles: ResponsiveSetting,
    defaultBpSettings: Partial<ResponsiveSetting>
  ): void {
    this.setHostStyles({
      backgroundColor: this.slice.settings!.styles.bgColor || '',
      marginTop: breakpointStyles.marginTop || defaultBpSettings.marginTop || '',
      marginRight: breakpointStyles.marginRight || defaultBpSettings.marginRight || '',
      marginLeft: breakpointStyles.marginLeft || defaultBpSettings.marginLeft || '',
      marginBottom: breakpointStyles.marginBottom || defaultBpSettings.marginBottom || '',
      paddingTop: breakpointStyles.paddingTop || defaultBpSettings.paddingTop || '',
      paddingRight: breakpointStyles.paddingRight || defaultBpSettings.paddingRight || '',
      paddingLeft: breakpointStyles.paddingLeft || defaultBpSettings.paddingLeft || '',
      paddingBottom: breakpointStyles.paddingBottom || defaultBpSettings.paddingBottom || ''
    });
  }

  private setHostStyles(styles: SliceBaseHostStyles): void {
    this.backgroundColor = styles.backgroundColor;
    this.marginTop = styles.marginTop;
    this.marginRight = styles.marginRight;
    this.marginLeft = styles.marginLeft;
    this.marginBottom = styles.marginBottom;
    this.paddingTop = styles.paddingTop;
    this.paddingRight = styles.paddingRight;
    this.paddingLeft = styles.paddingLeft;
    this.paddingBottom = styles.paddingBottom;
  }

  private initTabs(): void {
    this.tabsRange = Object.keys(this.slice.children)
      .filter(childKey => !this.slots[+childKey - 1].hidden && this.hasContent(this.slice.children[childKey][0]))
      .map(childKey => +childKey - 1);

    this.currentTab = this.tabsRange.find((t) => t === this.slice.settings?.general.defaultTab) ?? this.tabsRange[0];
    this.loadedTabs = Object.fromEntries(this.tabsRange.map((tab) => [tab, false]));
    this.loadedTabs[this.currentTab] = true;
  }

}

